import bus from '@/lib/event_bus';
import { get } from '@/lib/helpers';
import { getCustomerData, inititateReApplication } from '@/api';
import BigCookie from '@/business/models/big_cookie';
import Customer from '@/business/models/customer';
import MtProfileBuilder from '@/business/mt_profile_builder';
import MtProfile from '@/business/models/mt_profile';
import {
  clearCustomerDetails,
  storeCustomerDetail,
} from '@/store/actions/customer';
import { setLastBigCookieHitAt } from '@/store/actions/app_config';
import { layoutConfig } from '@/store/getters/app_config';
import { store } from '@/store';
import { clearFreoConnectOffersCache } from '@/lib/freo_connect_redirect';

/**
 * An expired application is restarted for the customer instead of being shown
 * to them. The reject screen used to carry that action behind an "Apply Now"
 * button; for an expired application there is nothing to decide, so the same
 * call is made on their behalf and they land on the quick profile form with a
 * notification explaining why they are being asked for their details again.
 *
 * The steps mirror RejectScreen.vue's reapplyApplication() exactly — same
 * profile reset, same endpoint, same analytics event, same destination — so an
 * expired customer reaches the form through the identical path the button took.
 */
export const EXPIRED_REAPPLY_MESSAGE =
  'Your application has expired. Please enter your details again.';

/**
 * Tried at most once per page load. /qual re-reads the customer on entry and
 * routes from what it finds, so a backend that still reports the application as
 * expired would send the customer back to the reject route — and without this
 * flag that would call re-apply again, and again. One attempt, then the reject
 * screen is allowed to load as it otherwise would.
 */
let reapplyAttempted = false;

export function hasAttemptedExpiredReapply(): boolean {
  return reapplyAttempted;
}

// Exported for tests and for a deliberate retry; nothing in the app calls it.
export function resetExpiredReapplyAttempt(): void {
  reapplyAttempted = false;
}

/**
 * Pulls the customer down again so everything downstream reads the state the
 * re-application produced rather than the one that sent them here.
 *
 * This is not optional. /qual's beforeEnter decides where to route from the
 * stored customer, and it asks for a refresh with override=false — which the
 * five-minute window in canCallBigCookie() turns into a no-op, because the app
 * fetched the customer moments ago to discover the rejection in the first place.
 * Left stale, that state still reads as expired and routes the customer
 * straight back to the reject screen they were just taken off.
 *
 * getCustomerData() is called directly rather than through retrieveBigCookie():
 * that helper is gated by the same throttle, and its promise never settles on a
 * response that is neither a 200 nor a thrown error — inside a router guard that
 * would hold the navigation open with nothing on screen.
 */
async function refreshCustomerAfterReapply(): Promise<void> {
  try {
    const response: any = await getCustomerData();
    if (response && response.ok && response.status === 200) {
      storeCustomerDetail({ payload: response.data });
      // Keeps the next throttled caller from spending a second round trip on
      // what was just fetched.
      setLastBigCookieHitAt({ payload: new Date().getTime() });
      // Refreshes the BigCookie and ApplicationState singletons the routing and
      // isApplicationExpired() both read from.
      BigCookie.getInstance().setBigCookie(response.data as BigCookie);
    }
  } catch (error) {
    // Routing then falls back to the stale state, which puts the customer on the
    // reject screen with the re-apply button — the behaviour without any of this.
    console.log('Could not refresh the customer after re-applying', error);
  }
}

/**
 * Re-applies for the customer whose application expired.
 *
 * Returns the route name to send them to, or null when the re-application could
 * not be made — in which case the caller must fall back to whatever it would
 * have done without this, so a failure here can never strand the customer.
 */
export async function restartExpiredApplication(): Promise<string | null> {
  if (reapplyAttempted) {
    return null;
  }
  const state: any = store.getState();
  const customerId =
    get(state, 'customer.customerId', '') ||
    BigCookie.getInstance().customerId ||
    '';
  // No customer id, no re-application: the endpoint is addressed by it.
  if (!customerId) {
    return null;
  }
  reapplyAttempted = true;
  // The previous decision is gone, so the partner offers cached against it must
  // not be reused.
  clearFreoConnectOffersCache();
  bus.$emit('loader', {
    show: true,
    message: 'Reapplying...',
  });
  try {
    const customer: Customer = Customer.getInstance();
    // Drops the profile the expired application was built from, then seeds the
    // new one with the identity we already hold, exactly as the button did.
    const mtProfile: MtProfileBuilder = MtProfileBuilder.getInstance();
    mtProfile.generateNewProfile();
    mtProfile
      .addProperty(MtProfile.MODEL_CUSTOMER_ID, customerId)
      .addProperty(
        MtProfile.MODEL_CUSTOMER,
        customer.phone,
        MtProfile.CUSTOMER_PHONE
      )
      .addProperty(
        MtProfile.MODEL_CUSTOMER,
        customer.firstName,
        MtProfile.CUSTOMER_FIRST_NAME
      )
      .addProperty(
        MtProfile.MODEL_CUSTOMER,
        customer.middleName,
        MtProfile.CUSTOMER_MIDDLE_NAME
      )
      .addProperty(
        MtProfile.MODEL_CUSTOMER,
        customer.lastName,
        MtProfile.CUSTOMER_LAST_NAME
      )
      .build();

    const response: any = await inititateReApplication({ customerId });
    if (!response || !response.ok || response.status !== 200) {
      return null;
    }
    clearCustomerDetails();
    bus.$emit('syncEvent', 'WEB.REJECT_APPLICATION.REAPPLY');
    await refreshCustomerAfterReapply();
    // qualHome rather than the quick profile route by name: it is the one route
    // both layouts define, and its own guard forwards to that layout's quick
    // profile screen — quick-profile-v2 on the default layout.
    return get(layoutConfig(), 'routes.qualHome', 'qual');
  } catch (error) {
    console.log('Re-application for the expired application failed', error);
    return null;
  } finally {
    bus.$emit('loader');
  }
}
